/**
 * Social Proof carousel
 * Desktop: infinite loop via clones + transform
 * Mobile: native horizontal scroll + snap
 * Scoped custom element — does not affect other sections
 */
if (!customElements.get('social-proof-carousel')) {
  class SocialProofCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.container = this.querySelector('[data-sp-cards]');
      this.currentEl = this.querySelector('[data-sp-current]');
      this.prevBtn = this.querySelector('[data-sp-prev]');
      this.nextBtn = this.querySelector('[data-sp-next]');
      this.total = Number(this.dataset.total) || 0;
      this.visible = Math.max(1, Number(this.dataset.visible) || 4);
      this.gap = Number(this.dataset.gap) || 12;
      this.domIndex = this.visible;
      this.isAnimating = false;
      this.mobileIndex = 0;
      this.scrollTimer = null;
      this.breakpoint = 999;

      if (!this.container || this.total === 0) return;

      this.onPrev = () => {
        if (this.isDesktop()) this.slideTo(this.domIndex - 1);
        else this.scrollToMobile(this.mobileIndex - 1);
      };

      this.onNext = () => {
        if (this.isDesktop()) this.slideTo(this.domIndex + 1);
        else this.scrollToMobile(this.mobileIndex + 1);
      };

      this.onResize = () => {
        this.domIndex = this.visible;
        this.mobileIndex = 0;
        this.init();
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize);

      this.init();
    }

    disconnectedCallback() {
      if (this.prevBtn && this.onPrev) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn && this.onNext) this.nextBtn.removeEventListener('click', this.onNext);
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.container && this.onMobileScroll) {
        this.container.removeEventListener('scroll', this.onMobileScroll);
      }
      if (this.scrollTimer) clearTimeout(this.scrollTimer);
    }

    pad(n) {
      return n < 10 ? '0' + n : '' + n;
    }

    isDesktop() {
      return window.innerWidth > this.breakpoint;
    }

    getCardWidth() {
      const card = this.container.querySelector('.social-proof__card:not(.sp-clone)');
      return card ? card.getBoundingClientRect().width + this.gap : 0;
    }

    getRealIndex(di) {
      return ((di - this.visible) % this.total + this.total) % this.total;
    }

    setupClones() {
      this.container.querySelectorAll('.sp-clone').forEach((c) => c.remove());
      const originals = Array.from(
        this.container.querySelectorAll('.social-proof__card:not(.sp-clone)')
      );
      if (originals.length === 0) return;

      for (let i = 0; i < this.visible; i++) {
        const c1 = originals[i % this.total].cloneNode(true);
        c1.classList.add('sp-clone');
        c1.setAttribute('aria-hidden', 'true');
        this.container.appendChild(c1);
      }

      for (let j = this.visible - 1; j >= 0; j--) {
        const c2 = originals[(this.total - this.visible + j + this.total) % this.total].cloneNode(true);
        c2.classList.add('sp-clone');
        c2.setAttribute('aria-hidden', 'true');
        this.container.insertBefore(c2, this.container.firstChild);
      }
    }

    applyTransform(animate) {
      const cw = this.getCardWidth();
      this.container.style.transition = animate
        ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        : 'none';
      this.container.style.transform = 'translateX(-' + this.domIndex * cw + 'px)';
    }

    checkLoop() {
      if (this.domIndex >= this.visible + this.total) {
        this.domIndex -= this.total;
        this.applyTransform(false);
        this.container.getBoundingClientRect();
      } else if (this.domIndex < this.visible) {
        this.domIndex += this.total;
        this.applyTransform(false);
        this.container.getBoundingClientRect();
      }
      this.isAnimating = false;
    }

    slideTo(newIndex) {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.domIndex = newIndex;
      this.applyTransform(true);
      if (this.currentEl) {
        this.currentEl.textContent = this.pad(this.getRealIndex(this.domIndex) + 1);
      }
      setTimeout(() => this.checkLoop(), 410);
    }

    onMobileScrollHandler() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const cards = this.container.querySelectorAll('.social-proof__card:not(.sp-clone)');
        const scrollLeft = this.container.scrollLeft;
        const maxScroll = this.container.scrollWidth - this.container.clientWidth;

        if (scrollLeft >= maxScroll - 5) {
          this.mobileIndex = this.total - 1;
        } else {
          let best = 0;
          let bestDiff = Infinity;
          cards.forEach((card, i) => {
            const diff = Math.abs(card.offsetLeft - scrollLeft);
            if (diff < bestDiff) {
              bestDiff = diff;
              best = i;
            }
          });
          this.mobileIndex = best;
        }
        if (this.currentEl) {
          this.currentEl.textContent = this.pad(this.mobileIndex + 1);
        }
      }, 20);
    }

    scrollToMobile(index) {
      this.mobileIndex = ((index % this.total) + this.total) % this.total;
      const cards = this.container.querySelectorAll('.social-proof__card:not(.sp-clone)');
      if (cards[this.mobileIndex]) {
        this.container.scrollLeft = cards[this.mobileIndex].offsetLeft;
      }
      if (this.currentEl) {
        this.currentEl.textContent = this.pad(this.mobileIndex + 1);
      }
    }

    init() {
      if (this.onMobileScroll) {
        this.container.removeEventListener('scroll', this.onMobileScroll);
      }

      if (this.isDesktop()) {
        this.domIndex = this.visible;
        this.setupClones();
        this.applyTransform(false);
        if (this.currentEl) {
          this.currentEl.textContent = this.pad(this.getRealIndex(this.domIndex) + 1);
        }
      } else {
        this.container.querySelectorAll('.sp-clone').forEach((c) => c.remove());
        this.container.style.transform = '';
        this.container.style.transition = '';
        this.mobileIndex = 0;
        if (this.currentEl) this.currentEl.textContent = '01';
        this.onMobileScroll = () => this.onMobileScrollHandler();
        this.container.addEventListener('scroll', this.onMobileScroll, { passive: true });
      }
    }
  }

  customElements.define('social-proof-carousel', SocialProofCarousel);
}
